import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Header } from '../common_v2/components/Header';
import { ButtonTheme, GenericAlertPopup, GenericPopup } from '@apply/components';
import { AutomaticAgreementStringBody, AgreementStringHeader } from '@apply/ui/src/lib/tables/draws/jsxConstants';
import { BrokerClientApi, DrawApi } from '@apply/api';
import { applicationInsights } from '../ApplicationInsights';
import { SignaturePage } from '@apply/components/src/components/controls/SignaturePage';
import { useDrawBudgetWithDraw } from '../home_v2/hooks/useDrawBudgetWithDraw';
import { ConfirmV2 } from '@apply/ui';
import { useAuthentication } from '../Login/hooks/useAuthentication';

export const PrincipalApproveDrawPage: React.FC = () => {
    const { authentication } = useAuthentication();
    const navigate = useNavigate();
    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);
    const drawApi = new DrawApi.DrawApi(applicationInsights);

    // Extract drawBudgetId and drawId from URL
    const pathParts = location.pathname.split('/');
    const drawBudgetId = parseInt(pathParts[2]);
    const drawId = parseInt(pathParts[4]);
    const { drawBudget, draw, drawInfoLoaded } = useDrawBudgetWithDraw(drawBudgetId, drawId);

    const [ipAddress, set_ipAddress] = useState<string>();
    const [isDrawApprovedOpen, set_isDrawApprovedOpen] = useState(false);
    const [disableActionButtons, set_disableActionButtons] = useState(true);
    const [isApprovalScenario, set_isApprovalScenario] = useState(false);
    const [showLoadError, set_showLoadError] = useState(false);

    useEffect(() => {
        // Get IP address on mount
        brokerClientApi.getIpAddress().then((response) => {
            if (response.statusCode !== 200) return;
            set_ipAddress(response.result);
        });
    }, []);

    useEffect(() => {
        if (!drawInfoLoaded) return;

        if (!drawBudget || !draw) {
            set_showLoadError(true);
            return;
        }

        // Check if this isn't an approval scenario, whether it SHOULD be -- i.e., if it's already
        // signed by the GC, but not by the approver, BUT the current user is not the approver
        const shouldBeApprovalScenario =
            draw?.generalContractorSignatureId != null &&
            draw?.generalContractorSignatureRequiresApproval === true &&
            draw?.borrowerSignatureId == null &&
            drawBudget?.isCurrentUserDrawApprover !== true;
        if (shouldBeApprovalScenario) {
            GenericAlertPopup({
                title: 'Borrower Approval Required',
                children: 'This draw request currently can only be approved by the borrower.',
                width: '350px',
                handleClose: () => {
                    navigate('/home');
                },
            });
            return;
        }

        // Check if this is an approval scenario based on draw state and current user
        // (a) GC has signed, (b) GC signature requires approval, (c) Principal hasn't signed yet,
        // (d) Current user is the approving user
        set_isApprovalScenario(
            draw?.generalContractorSignatureId != null &&
                draw?.generalContractorSignatureRequiresApproval === true &&
                draw?.borrowerSignatureId == null &&
                drawBudget?.isCurrentUserDrawApprover === true
        );

        set_disableActionButtons(!drawBudget || !draw);
    }, [drawInfoLoaded]);

    const onDeclineAgreement = async () => {
        set_disableActionButtons(true);

        if (isApprovalScenario) {
            const response = await drawApi.deleteDraw(drawId);

            if (response.statusCode !== 200) {
                GenericAlertPopup({
                    title: 'Error',
                    children: 'An unexpected error occurred while canceling the draw request. Please try again.',
                    width: '350px',
                });
                set_disableActionButtons(false);
                return;
            }
        }

        navigate('/home');
    };

    const onSubmitAgreement = async () => {
        set_disableActionButtons(true);

        const response = await drawApi.signGCRequestedDraw({
            drawId: drawId,
            fontId: 1,
            ipAddress: ipAddress,
        });

        if (response.statusCode !== 200) {
            set_disableActionButtons(false);
            return;
        }

        set_isDrawApprovedOpen(true);
    };

    const handleDrawApprovedClose = () => {
        set_isDrawApprovedOpen(false);
        navigate('/home');
    };

    const drawApprovedActionButtons = [
        {
            id: 'closeDrawApprovedButton',
            theme: ButtonTheme.Primary,
            disabled: false,
            children: 'Close',
            width: '150px',
            handleClick: handleDrawApprovedClose,
        },
    ];

    return (
        <>
            <div>
                <Header />
                <SignaturePage
                    agreementHeader={<AgreementStringHeader />}
                    agreementBody={<AutomaticAgreementStringBody />}
                    acceptButtonText={!drawInfoLoaded ? 'Please wait...' : isApprovalScenario ? 'Approve' : 'Accept'}
                    onSubmitAgreement={onSubmitAgreement}
                    onDeclineAgreement={onDeclineAgreement}
                    disableButtons={disableActionButtons}
                    firstName={authentication?.user.firstName ?? ''}
                    lastName={authentication?.user.lastName ?? ''}
                />
            </div>
            <GenericPopup
                id={'DrawApprovedPopup'}
                isOpen={isDrawApprovedOpen}
                actionButtons={drawApprovedActionButtons}
                handleClose={handleDrawApprovedClose}
                aria-labelledby='alert-dialog-title'
                hideOnOutsideClick={true}
                showCloseButton={true}
                showTitle={true}
                title='Draw Approved'
                width='400px'
                height='230px'
                maxWidth='90%'
                maxHeight='60%'
                buttonAlignment='center'
                wrapTitle={true}
            >
                <div>
                    <h5>Thank you!</h5>
                    <div style={{ paddingTop: '10px' }}>
                        The draw request has been approved and submitted. We will endeavor to fund within three business days.
                    </div>
                </div>
            </GenericPopup>
            <ConfirmV2
                id='LoadErrorConfirmPopup'
                title='Error'
                okLabel='Retry'
                cancelLabel='Cancel'
                open={showLoadError}
                children={<p>Could not load draw request info. Would you like to try again?</p>}
                onConfirm={() => window.location.reload()}
                onCancel={() => navigate('/home')}
            />
        </>
    );
};
